import React, { useState, useEffect } from 'react';
import DateRange from '../DateRange/DateRange';
import { useNavigate } from "react-router-dom";
import './PaymentSearch.css';
import { AmountRange } from '../Filters/AmountFilter/AmountRange';
import { validateAmountRange } from '../../validators/AmountRangeValidator';
import { alphanumericValidator } from '../../validators/InputValidator';
import { ValidatedInput } from '../ValidatedInput.js/ValidatedInput';
import { useCompany } from '../CompanySelection/CompanyContext';
import { getUrl } from '../../util/EfupUtility';
import { cancelRequest, makeRequest, RequestParameters } from '../../service/PaymentSearchService';
import { defaultFilter } from '../PaymentFilter/PaymentFilter';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { Alert } from '../Alert/Alert';

const PaymentSearch = props => {

    const [paymentNumber, setPaymentNumber] = useState("");
    const [partyName, setPartyName] = useState("");
    const [startDate, setStartDate] = useState();
    const [endDate, setEndDate] = useState();
    const [disbursementUuid, setDisbursementUuid] = useState("");
    const [startAmount, setStartAmount] = useState('')
    const [endAmount, setEndAmount] = useState('')
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [searchCanceled, setSearchCanceled] = useState(false);
    const { companyNumber } = useCompany();
    const workflow = 'ALL';

    let navigate = useNavigate();

    const canSubmit = (paymentNumber || partyName || disbursementUuid || (startDate && endDate) || (startAmount && endAmount)) && (Object.keys(errors).length === 0)

    const customAmountRangeClasses = {
        inputContainer: 'cb-form-control',
        inputSeparator: 'input-separator-large'
    }

    function errorAmountRangeInput() {
        let isError = false;
        if ((startAmount && !endAmount) || (!startAmount && endAmount)) {
            setErrors({ ...errors, amountRange: "Both min and max amount required" })
            isError = true;
        }
        // General requirements for Amount Range
        if (startAmount && endAmount) {
            let validationErrors = validateAmountRange(startAmount, endAmount)

            if (validationErrors) {
                setErrors({ ...errors, amountRange: validationErrors })
                isError = true;
            }
        }
        return isError;
    }

    function validateAmountRangeInput() {
        if (!errorAmountRangeInput() || (!startAmount && !endAmount)) {
            removeError("amountRange")
        }
    }

    // Determines when to handle amount range validation
    useEffect(() => {
        validateAmountRangeInput()
    }, [startAmount, endAmount]);     // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if ((startDate && !endDate) || (!startDate && endDate)) {
            setErrors({ ...errors, endDate: "Both start and end date are required" })
        }

        if ((!startDate && endDate)) {
            setErrors({ ...errors, startDate: "Both start and end date are required" })
        }

        // If the user entered an invalid range then removed both inputs, remove error
        if (!startDate && !endDate) {
            removeError("startDate")
            removeError("endDate")
        }

        // General requirements for Amount Range
        if (startDate && endDate) {
            removeError("startDate")
            removeError("endDate")
        }
    }, [startDate, endDate]);     // eslint-disable-line react-hooks/exhaustive-deps

    function handleInputChange(errorKey, inputValue, valueSetter, validator) {
        let validationError;
        if (validator) {
            validationError = validator(inputValue)
        }

        valueSetter(inputValue)

        if (validationError) {
            setErrors({ ...errors, [errorKey]: validationError })
        } else {
            removeError(errorKey)
        }
    }

    function removeError(error) {
        setErrors(current => {
            // create copy of error state
            const copy = { ...current }
            // remove error key from object
            delete copy[error]
            return copy;
        });
    }

    function handlePaymentSearch() {
        setIsLoading(true);
        let searchState = { ...defaultFilter(30, "PaymentSearch"), ...getSearchFilters() };

        let paymentOptions = { workflow, ...searchState };
        let request = RequestParameters({}, paymentOptions);

        makeRequest(request, companyNumber, dataHandler, errorHandler);
    }

    function dataHandler(data) {
        if (data) {
            let searchState = getSearchFilters();

            navigate(getUrl(companyNumber, '/payments'), {
                state: {
                    data,
                    search: {
                        ...searchState
                    },
                    source: "PaymentSearch",
                }
            })
        }
        setIsLoading(false);
    }

    function getSearchFilters() {
        let searchState = {
            paymentNumber: paymentNumber,
            partyName: partyName,
            disbursementUuid: disbursementUuid,
        };
        if (startDate) {
            searchState.startDate = startDate;
        }
        if (endDate) {
            searchState.endDate = endDate;
        }
        if (startAmount) {
            searchState.disbursementAmountMin = startAmount;
        }
        if (endAmount) {
            searchState.disbursementAmountMax = endAmount;
        }

        return searchState;
    }

    function errorHandler(err) {
        setIsLoading(false);
    }

    function handleSearchCancel() {
        cancelRequest();
        setSearchCanceled(true);
        setIsLoading(false);
    }

    return (
        <div className="payment-search-wrapper dashBox">
            <div className="payment-search-title cb-row cb-form-group" data-testid="payment-search-payment-lookup-label">Payment Look Up</div>
            {isLoading ? <div className='payment-search-loading-spinner'>
                <LoadingSpinner blue testId='payment-search-loading-spinner' />
                <p className='loading-spinner-text'>Search in progress...</p>
                <p className='loading-spinner-text link' data-testid='payment-search-loading-cancel-link' onClick={() => handleSearchCancel()}>Cancel search</p>
            </div>
                :
                <>{searchCanceled &&
                    <Alert type="success" handleOnClose={() => setSearchCanceled(false)} dataTestId='payment-search'>
                        <div style={{ fontWeight: '500' }}>Your search was successfully canceled.</div>
                    </Alert>}
                    <ValidatedInput
                        label="Payment Number"
                        errorKey="paymentNumber"
                        formName="Payment Search"
                        maxLength={50}
                        value={paymentNumber}
                        valueSetter={setPaymentNumber}
                        validator={alphanumericValidator}
                        onChange={handleInputChange}
                        errors={errors?.paymentNumber}
                    />
                    <ValidatedInput
                        label="Party Name"
                        errorKey="partyName"
                        formName="Payment Search"
                        maxLength={255}
                        value={partyName}
                        valueSetter={setPartyName}
                        validator={alphanumericValidator}
                        onChange={handleInputChange}
                        errors={errors?.partyName}
                    />
                    <div className={`payment-search-date-range cb-form-group${errors?.startDate ? " payment-search-start-date-error" : ""}${errors?.endDate ? " payment-search-end-date-error" : ""}`} >
                        <label className="cb-form-control-label" data-testid="payment-search-date-range-label">Created Date</label>
                        <DateRange startDate={startDate} setStartDate={setStartDate} endDate={endDate} setEndDate={setEndDate} isClearable />
                        {errors?.startDate && (
                            <div className="cb-input-error" data-testid='efup-payment-search-date-range-errors'>{errors?.startDate}</div>
                        )}
                        {errors?.endDate && (
                            <div className="cb-input-error" data-testid='efup-payment-search-date-range-errors'>{errors?.endDate}</div>
                        )}
                    </div>
                    <ValidatedInput
                        label="Disbursement UUID"
                        errorKey="disbursementUuid"
                        formName="Payment Search"
                        maxLength={36}
                        value={disbursementUuid}
                        valueSetter={setDisbursementUuid}
                        validator={alphanumericValidator}
                        onChange={handleInputChange}
                        errors={errors?.disbursementUuid}
                    />
                    <div className="payment-search-disbursement-amount cb-form-group" >
                        <label data-testid="payment-search-amount-label" className="cb-form-control-label">Amount</label>
                        <AmountRange
                            startAmount={startAmount}
                            endAmount={endAmount}
                            errors={errors?.amountRange}
                            minAndMaxRequired={true}
                            customClasses={customAmountRangeClasses}
                            onStartChange={setStartAmount}
                            onEndChange={setEndAmount}
                        />
                    </div>
                    <div className="efup-payment-search-footer">
                        <button
                            type="button"
                            className="cb-btn cb-btn-outline-secondary payment-search-submit"
                            data-testid="payment-search-submit"
                            disabled={!canSubmit}
                            onClick={handlePaymentSearch}>
                            Search
                        </button>
                    </div></>}
        </div>
    );
}

export default React.memo(PaymentSearch);